import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Ticket, Copy, Loader2, Trash2, Search, Info, Check } from "lucide-react";
import { platformApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { fmtDate, apiError } from "@/lib/format";
import { LoadingState, ErrorState, EmptyState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS_STYLES = {
  active: "bg-emerald-100 text-emerald-700",
  redeemed: "bg-slate-200 text-slate-600",
  expired: "bg-amber-100 text-amber-700",
  revoked: "bg-red-100 text-red-700",
};

function discountLabel(d) {
  if (d.discount_type === "percent") return `${Number(d.percent_off)}% off`;
  if (d.amount_off != null) return `£${Number(d.amount_off).toFixed(2)} off`;
  return "—";
}

function copy(text) {
  navigator.clipboard?.writeText(text).then(
    () => toast.success("Code copied"),
    () => toast.error("Could not copy"),
  );
}

export default function DiscountsTab() {
  const { data: companiesData } = useAsync(() => platformApi.companies({ include_archived: false }), []);
  const { data, loading, error, reload } = useAsync(() => platformApi.discounts(), []);

  const [target, setTarget] = useState("");          // "" = generic, else company id
  const [companyQuery, setCompanyQuery] = useState("");
  const [type, setType] = useState("percent");
  const [value, setValue] = useState("");
  const [duration, setDuration] = useState("once");
  const [expiry, setExpiry] = useState("");
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);     // the newly generated code (dialog)

  const companies = useMemo(() => {
    const list = Array.isArray(companiesData) ? companiesData.filter((c) => !c.is_archived) : [];
    const q = companyQuery.trim().toLowerCase();
    const filtered = q
      ? list.filter((c) => `${c.name || ""} ${c.company_code || ""}`.toLowerCase().includes(q))
      : list;
    return filtered.slice(0, 50);
  }, [companiesData, companyQuery]);

  const discounts = (data?.discounts) || [];

  async function create() {
    const num = Number(value);
    if (!num || num <= 0) { toast.error("Enter a discount amount"); return; }
    if (type === "percent" && num > 100) { toast.error("Percentage can't exceed 100"); return; }
    setSaving(true);
    try {
      const body = { discount_type: type, value: num, duration };
      if (target) body.target_company_id = target;
      if (expiry) body.expires_at = new Date(`${expiry}T23:59:59`).toISOString();
      const res = await platformApi.createDiscount(body);
      setCreated(res?.data || res);
      setValue("");
      setExpiry("");
      reload();
    } catch (e) {
      toast.error(apiError(e, "Could not create discount code"));
    } finally {
      setSaving(false);
    }
  }

  async function revoke(id) {
    try {
      await platformApi.revokeDiscount(id);
      toast.success("Code revoked");
      reload();
    } catch (e) {
      toast.error(apiError(e, "Could not revoke code"));
    }
  }

  return (
    <div className="space-y-4" data-testid="platform-discounts">
      <Card className="flex items-start gap-3 border-sky-100 bg-sky-50/60 p-4">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" />
        <p className="text-sm text-slate-600">
          Discount codes apply to <span className="font-semibold">card subscriptions on the website</span>. App Store / Google
          Play purchases use their own promo offers and cannot redeem these codes — so point marketing at the web sign-up.
        </p>
      </Card>

      {/* Create a code */}
      <Card className="p-5" data-testid="discount-create-card">
        <p className="mb-4 flex items-center gap-2 font-semibold text-slate-800">
          <Ticket className="h-5 w-5 text-slate-700" /> Create a discount code
        </p>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Target */}
          <div>
            <Label className="text-xs text-slate-500">Who can use it</Label>
            <Select value={target || "generic"} onValueChange={(v) => setTarget(v === "generic" ? "" : v)}>
              <SelectTrigger className="mt-1" data-testid="discount-target"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="generic">Any new subscriber (single use)</SelectItem>
                <div className="px-2 py-1.5">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                    <Input
                      value={companyQuery}
                      onChange={(e) => setCompanyQuery(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      placeholder="Search companies…"
                      className="h-8 pl-7 text-sm"
                      data-testid="discount-company-search"
                    />
                  </div>
                </div>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`discount-company-${c.id}`}>
                    {c.name} · {c.company_code}
                  </SelectItem>
                ))}
                {companies.length === 0 && (
                  <p className="px-3 py-2 text-xs text-slate-400">No matching companies</p>
                )}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-slate-400">
              {target ? "Locked to this company — only they can redeem it." : "First person to redeem it wins; works once."}
            </p>
          </div>

          {/* Discount type + value */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="mt-1" data-testid="discount-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Percentage</SelectItem>
                  <SelectItem value="amount">Fixed £ amount</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">{type === "percent" ? "Percent off" : "Amount off (£)"}</Label>
              <Input
                type="number" min="1" step={type === "percent" ? "1" : "0.01"}
                value={value} onChange={(e) => setValue(e.target.value)}
                placeholder={type === "percent" ? "25" : "30.00"}
                className="mt-1" data-testid="discount-value"
              />
            </div>
          </div>

          {/* Duration */}
          <div>
            <Label className="text-xs text-slate-500">How long it applies</Label>
            <Select value={duration} onValueChange={setDuration}>
              <SelectTrigger className="mt-1" data-testid="discount-duration"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="once">First payment only</SelectItem>
                <SelectItem value="forever">Every renewal (ongoing)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Expiry */}
          <div>
            <Label className="text-xs text-slate-500">Expires (optional)</Label>
            <Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} className="mt-1" data-testid="discount-expiry" />
            <p className="mt-1 text-xs text-slate-400">Must be redeemed by this date. Leave blank for no expiry.</p>
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <Button onClick={create} disabled={saving} data-testid="discount-create-btn">
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Ticket className="mr-1.5 h-4 w-4" />}
            Generate code
          </Button>
        </div>
      </Card>

      {/* Issued codes */}
      <Card className="p-5" data-testid="discount-list-card">
        <p className="mb-3 font-semibold text-slate-800">Issued codes</p>
        {loading ? (
          <LoadingState label="Loading discount codes…" />
        ) : error ? (
          <ErrorState message={error} onRetry={reload} />
        ) : discounts.length === 0 ? (
          <EmptyState icon={Ticket} title="No discount codes yet" message="Generate your first marketing code above." />
        ) : (
          <div className="divide-y divide-slate-100">
            {discounts.map((d) => (
              <div key={d.id} className="flex flex-wrap items-center justify-between gap-3 py-3" data-testid={`discount-row-${d.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-semibold text-slate-900" data-testid={`discount-code-${d.id}`}>{d.code}</span>
                    <button onClick={() => copy(d.code)} className="text-slate-400 hover:text-slate-700" title="Copy code" data-testid={`discount-copy-${d.id}`}>
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                    <Badge className={STATUS_STYLES[d.status] || "bg-slate-100 text-slate-600"} data-testid={`discount-status-${d.id}`}>{d.status}</Badge>
                  </div>
                  <p className="mt-0.5 truncate text-xs text-slate-500">
                    {discountLabel(d)} · {d.duration === "forever" ? "every renewal" : "first payment"} ·{" "}
                    {d.target_company_name ? `for ${d.target_company_name}` : "any new subscriber"}
                    {d.expires_at ? ` · expires ${fmtDate(d.expires_at)}` : ""}
                  </p>
                </div>
                {d.status === "active" ? (
                  <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" onClick={() => revoke(d.id)} data-testid={`discount-revoke-${d.id}`}>
                    <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Revoke
                  </Button>
                ) : (
                  <span className="text-xs text-slate-400">{d.times_redeemed > 0 ? "used" : ""}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Generated code dialog */}
      <Dialog open={!!created} onOpenChange={(o) => { if (!o) setCreated(null); }}>
        <DialogContent data-testid="discount-created-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Check className="h-5 w-5 text-emerald-600" /> Code ready to share</DialogTitle>
            <DialogDescription>
              Give this code to {created?.target_company_name ? created.target_company_name : "your chosen subscriber"}. They enter it at
              web checkout. It works once{created?.expires_at ? `, and expires on ${fmtDate(created.expires_at)}` : ""}.
            </DialogDescription>
          </DialogHeader>
          {created && (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <span className="font-mono text-xl font-bold tracking-wider text-slate-900" data-testid="discount-created-code">{created.code}</span>
              <Button size="sm" variant="outline" onClick={() => copy(created.code)} data-testid="discount-created-copy">
                <Copy className="mr-1.5 h-4 w-4" /> Copy
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
